
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { BarChart3, History, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { ImageWithFallback } from '@/components/common/ImageWithFallback';
import { MarketAnalysisUsageWidget } from '@/components/market-analysis/usage/MarketAnalysisUsageWidget';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

const menuItems = [
  {
    title: "AI Market Analysis",
    url: "/market-analysis",
    icon: BarChart3,
  },
  {
    title: "Analysis History",
    url: "/market-analysis/history",
    icon: History,
  },
];

export const MarketAnalysisSidebarNew = () => {
  const { t } = useTranslation();
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const location = useLocation();
  const currentPath = location.pathname;
  const isMobile = useIsMobile();

  const isActive = (path: string) => currentPath === path;

  return (
    <Sidebar className="border-r border-border/50 bg-gradient-to-b from-background via-background/95 to-muted/20 backdrop-blur-xl">
      <SidebarHeader className="border-b border-border/30 bg-gradient-to-r from-muted/20 to-background/50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className={cn(
          "mx-0 px-6 py-4 rounded-none my-0 relative transition-all duration-300"
        )}>
          <div className={cn(
            "relative transition-all duration-300",
            isMobile ? "w-16 h-16" : "w-20 h-20",
            isCollapsed && "scale-75"
          )}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent rounded-2xl blur-xl animate-pulse" />
            <ImageWithFallback
              src="https://lbvhywxvthwgiebjsrlr.supabase.co/storage/v1/object/public/logoexchangeplatformurl//logo%20trade%20bot%20365.png"
              alt="Trade Bot 365 Logo"
              className={cn("object-contain transition-all duration-300 relative z-10", 
                isCollapsed ? "scale-90" : "scale-100",
                isMobile ? "w-16 h-16" : "w-20 h-20"
              )}
              fallbackElement={
                <div className={cn("bg-gradient-to-br from-emerald-500 to-blue-500 rounded-lg flex items-center justify-center",
                  isMobile ? "w-16 h-16" : "w-20 h-20"
                )}>
                  <BarChart3 className={cn("text-white", isMobile ? "w-8 h-8" : "w-10 h-10")} />
                </div>
              }
              loading="eager"
              priority="high"
            />
          </div>
          <button 
            onClick={toggleSidebar} 
            className={cn(
              "p-2 rounded-xl hover:bg-primary/10 transition-all duration-300 absolute right-3 top-3 group hover:scale-105", 
              !isCollapsed ? "" : "hidden"
            )}
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              {isCollapsed ? <ChevronRight size={18} className="relative z-10" /> : <ChevronLeft size={18} className="relative z-10" />}
            </div>
          </button>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2 py-4">
        <SidebarGroup>
          <SidebarGroupLabel className="text-sm font-semibold text-muted-foreground/80 mb-3 px-3 uppercase tracking-wider">
            Market Analysis
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {menuItems.map((item) => {
                const itemIsActive = isActive(item.url);
                return (
                  <SidebarMenuItem key={item.title} className="group/item">
                    <SidebarMenuButton 
                      asChild 
                      isActive={itemIsActive}
                      className={cn(
                        "relative rounded-xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]",
                        itemIsActive 
                          ? "bg-gradient-to-r from-primary/15 via-primary/10 to-transparent border border-primary/20 shadow-md shadow-primary/10" 
                          : "hover:bg-gradient-to-r hover:from-muted/50 hover:to-transparent hover:border-border/30"
                      )}
                    >
                      <NavLink to={item.url} className="flex items-center gap-3 w-full">
                        <div className={cn(
                          "p-2 rounded-lg transition-all duration-300 group-hover/item:scale-110",
                          itemIsActive 
                            ? "bg-primary/20 text-primary shadow-sm" 
                            : "text-muted-foreground group-hover/item:bg-muted/30 group-hover/item:text-foreground"
                        )}>
                          <item.icon size={18} />
                        </div>
                        {!isCollapsed && (
                          <span className={cn(
                            "font-medium transition-all duration-300",
                            itemIsActive ? "text-foreground" : "text-muted-foreground group-hover/item:text-foreground"
                          )}>
                            {item.title}
                          </span>
                        )}
                        {itemIsActive && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-primary to-primary/50 rounded-r-full shadow-lg shadow-primary/30" />
                        )}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Usage Information Section */}
        <SidebarGroup className="mt-6">
          {!isCollapsed && (
            <SidebarGroupLabel className="text-sm font-semibold text-muted-foreground/80 mb-3 px-3 uppercase tracking-wider">
              Usage
            </SidebarGroupLabel>
          )}
          <SidebarGroupContent>
            <MarketAnalysisUsageWidget className="mx-1" />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <div style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} className="pb-2" />
    </Sidebar>
  );
};
